import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useRecoilValue } from "recoil";
import { authState } from "../../Recoil/RecoilState";
import { reportLostItem } from "../../services/lostFoundApi";
import {
  ACCEPTED_PHOTO_TYPES,
  MAX_UPLOAD_BYTES,
  OBJECT_CLASSES,
  errorText,
  titleCase,
} from "./lostFoundConstants";

const EMPTY = { description: "", reporter_name: "", reporter_email: "", class_name: "", lost_after: "" };

/**
 * File a lost report from a photo and/or a description. Candidates are matched on
 * object type, colour, when the item was lost and (with a photo) how it looks.
 */
const ReportLostItemDialog = ({ open, onClose, onFiled }) => {
  const { token } = useRecoilValue(authState);
  const [values, setValues] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setValues(EMPTY);
      setPhoto(null);
      setErrors({});
      setSubmitError(null);
    }
  }, [open]);

  // Object URLs are not garbage-collected; revoke each one when the photo changes.
  useEffect(() => {
    if (!photo) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const setField = (field) => (event) => {
    setValues((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined, form: undefined }));
  };

  const pickPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
      setErrors((current) => ({ ...current, photo: "Must be a JPEG, PNG, WebP or BMP image." }));
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setErrors((current) => ({
        ...current,
        photo: `At most ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB — this one is ${(file.size / (1024 * 1024)).toFixed(1)} MB.`,
      }));
      return;
    }
    setErrors((current) => ({ ...current, photo: undefined, form: undefined }));
    setPhoto(file);
  };

  const hasEnough = Boolean(photo) || Boolean(values.description.trim());
  const emailValid = useMemo(() => {
    const email = values.reporter_email.trim();
    return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, [values.reporter_email]);

  const submit = async () => {
    const found = {};
    if (!hasEnough) found.form = "Provide a photo, a description, or both.";
    if (!emailValid) found.reporter_email = "Not a valid email address";
    setErrors(found);
    if (Object.keys(found).length) return;

    setSaving(true);
    setSubmitError(null);
    try {
      const report = await reportLostItem(
        {
          photo,
          description: values.description.trim(),
          reporterName: values.reporter_name.trim(),
          reporterEmail: values.reporter_email.trim(),
          className: values.class_name,
          // datetime-local is browser time without a zone; send it as an ISO instant.
          lostAfter: values.lost_after ? new Date(values.lost_after).toISOString() : "",
        },
        token
      );
      onFiled(report);
    } catch (err) {
      setSubmitError(errorText(err, "Could not file the report."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Report a lost item</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                height: 220, mb: 2, borderRadius: 1, bgcolor: "action.hover",
                display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
              }}
            >
              {preview ? (
                <img src={preview} alt="Preview of the lost item" style={{ maxWidth: "100%", maxHeight: "100%" }} />
              ) : (
                <Typography variant="caption" color="text.secondary">No photo selected</Typography>
              )}
            </Box>
            <Button component="label" variant="outlined" fullWidth startIcon={<PhotoCameraIcon />}>
              {photo ? "Choose a different photo" : "Choose a photo"}
              <input type="file" hidden accept={ACCEPTED_PHOTO_TYPES.join(",")} onChange={pickPhoto}
                aria-label="Photo of the lost item" />
            </Button>
            {photo && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" noWrap>{photo.name}</Typography>
                <Button size="small" color="inherit" onClick={() => setPhoto(null)}>Remove</Button>
              </Stack>
            )}
            {errors.photo && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>{errors.photo}</Typography>
            )}
          </Grid>

          <Grid item xs={12} md={7}>
            <Stack spacing={2}>
              <TextField label="Description" value={values.description} onChange={setField("description")}
                fullWidth multiline minRows={2} placeholder="Black leather bag with a front zip pocket"
                helperText="Colour, material and distinguishing marks. A colour word is used for matching when there is no photo." />
              <TextField select label="Object type" value={values.class_name} onChange={setField("class_name")} fullWidth
                helperText="Same-type items rank higher.">
                <MenuItem value="">Not sure</MenuItem>
                {OBJECT_CLASSES.map((value) => (
                  <MenuItem key={value} value={value}>{titleCase(value)}</MenuItem>
                ))}
              </TextField>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField label="Owner name" value={values.reporter_name} onChange={setField("reporter_name")} fullWidth />
                <TextField label="Owner email" type="email" value={values.reporter_email} onChange={setField("reporter_email")}
                  error={Boolean(errors.reporter_email)} helperText={errors.reporter_email} fullWidth />
              </Stack>
              <TextField label="Lost after" type="datetime-local" value={values.lost_after} onChange={setField("lost_after")}
                fullWidth InputLabelProps={{ shrink: true }}
                helperText="Optional. Items found before this time are left out." />
            </Stack>
          </Grid>
        </Grid>
        {errors.form && <Alert severity="warning" sx={{ mt: 2 }}>{errors.form}</Alert>}
        {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" disabled={saving} onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving || !hasEnough} onClick={submit}>
          {saving ? "Filing…" : "File report"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportLostItemDialog;
